"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_CONFIG_SCHEMA = void 0;
const get_position_1 = require("../util/get-position");
const types_1 = require("@refinitiv-data/types");
exports.DEFAULT_CONFIG_SCHEMA = {
    platform: {
        default: types_1.DEFAULT_CONFIG_PLATFORM,
        doc: 'Environment variable which platform we are working on (beta/prod/feat1/....)',
        env: 'RDPLIB_ENV',
        format: String,
    },
    dir: {
        default: types_1.DEFAULT_CONFIG_DIR,
        doc: 'Directory where configuration files are saved',
        env: 'RDPLIB_ENV_DIR',
        format: String,
    },
    'config-change-notifications-enabled': {
        default: false,
        doc: 'Is watch mode for configuration files is enabled',
        format: Boolean,
    },
    http: {
        'request-timeout': {
            default: 10,
            doc: 'Max timeout for any HTTP request sent by a library',
            format: Number,
        },
    },
    logs: {
        level: {
            default: 'info',
            doc: 'Level of logs to be printed',
            format: [
                0,
                1,
                2,
                3,
                4,
                5,
                'trace',
                'debug',
                'info',
                'warn',
                'error',
                'silent',
                'TRACE',
                'DEBUG',
                'INFO',
                'WARN',
                'ERROR',
                'SILENT',
            ],
        },
        filter: {
            default: '*',
            doc: 'Filter logs by module name. By default all logs are printed',
            format: String,
        },
        transports: {
            console: {
                enabled: {
                    default: true,
                    doc: 'Defines if logs will be written into the console',
                    format: Boolean,
                },
            },
            file: {
                enabled: {
                    default: true,
                    doc: 'Defines if logs will be written into the file',
                    format: Boolean,
                },
                name: {
                    default: 'refinitiv-data-lib',
                    doc: 'File to save logs of the RDP TS Library',
                    format: String,
                },
                size: {
                    default: '10M',
                    doc: 'Maximum size of a single log file',
                    format: String,
                },
                interval: {
                    default: '1d',
                    doc: 'Period of log files rotation. Default to every midnight',
                    format: String,
                },
                maxFiles: {
                    default: 10,
                    doc: 'Maximum count of log files for te current process',
                    format: Number,
                },
                maxSize: {
                    default: '1G',
                    doc: 'Maximum total size of all log files together for the current process',
                    format: String,
                },
                history: {
                    default: 'log-rotation',
                    doc: 'Name of the file to save logs rotation history',
                    format: String,
                },
            },
        },
    },
    sessions: {
        platform: {
            'default-session': {
                'auto-reconnect': {
                    default: false,
                    doc: 'This flag defines should the session automatically re-authenticate when an access token becomes expired',
                },
                'base-url': {
                    default: 'https://api.refinitiv.com',
                    doc: 'Platform session base url',
                    format: String,
                },
                'realtime-distribution-system': {
                    url: {
                        default: '',
                        doc: 'Real-Time Distribution System url.',
                        format: String,
                    },
                    dacs: {
                        username: {
                            default: '',
                            doc: 'Default user name to be used for the connection through the Real-Time Distribution System.',
                            format: String,
                        },
                        'application-id': {
                            default: '',
                            doc: 'Default application id to be used for the connection through the Real-Time Distribution System.',
                            format: String,
                        },
                        position: {
                            default: (0, get_position_1.getPosition)(),
                            doc: 'Default position identifier for the Real-Time Distribution System.',
                            format: String,
                        },
                    },
                },
                auth: {
                    v1: {
                        url: {
                            default: '/auth/oauth2/v1',
                            doc: 'Base url path for password "Auth" endpoint',
                            format: String,
                        },
                        authorize: {
                            default: '/authorize',
                            doc: 'Subpath for Auth endpoint to authorize a user',
                            format: String,
                        },
                        token: {
                            default: '/token',
                            doc: 'Subpath for Auth endpoint to retrieve an access token',
                            format: String,
                        },
                    },
                    v2: {
                        url: {
                            default: '/auth/oauth2/v2',
                            doc: 'Base url path for client credentials "Auth" endpoint',
                            format: String,
                        },
                        token: {
                            default: '/token',
                            doc: 'Subpath for Auth endpoint to retrieve an access token',
                            format: String,
                        },
                    },
                },
            },
        },
        desktop: {
            'default-session': {
                'base-url': {
                    default: 'http://127.0.0.1:9000',
                    doc: 'Desktop session base url',
                    env: 'DP_PROXY_BASE_URL',
                    format: String,
                },
                'platform-paths': {
                    rdp: {
                        default: '/api/rdp',
                        doc: 'RDP platform root path',
                        format: String,
                    },
                    udf: {
                        default: '/api/udf',
                        doc: 'UDF root path',
                        format: String,
                    },
                    tsi: {
                        default: '/api/rdp/data/historical-pricing/v1/metadata',
                        doc: 'TSI root path',
                        format: String,
                    },
                },
                'handshake-url': {
                    default: '/api/handshake',
                    doc: 'Handshake endpoint for desktop-session handshake',
                    format: String,
                },
            },
        },
    },
    apis: {
        data: {
            'historical-pricing': {
                url: {
                    default: '/data/historical-pricing/v1',
                    doc: 'Base url path for "HistoricalPricing" endpoint',
                    format: String,
                },
                endpoints: {
                    events: {
                        default: '/views/events',
                        doc: 'Subpath for HistoricalPricing endpoint to retrieve the "events" data',
                        format: String,
                    },
                    'interday-summaries': {
                        default: '/views/interday-summaries',
                        doc: 'Subpath for HistoricalPricing endpoint to retrieve the "events" data',
                        format: String,
                    },
                    'intraday-summaries': {
                        default: '/views/intraday-summaries',
                        doc: 'Subpath for HistoricalPricing endpoint to retrieve the "events" data',
                        format: String,
                    },
                },
            },
            'quantitative-analytics-financial-contracts': {
                url: {
                    default: '/data/quantitative-analytics/v1',
                    doc: 'Base url path for the "financial-contracts" content object',
                    format: String,
                },
                endpoints: {
                    'financial-contracts': {
                        default: '/financial-contracts',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "financial-contracts" data',
                        format: String,
                    },
                },
            },
            'quantitative-analytics-curves-and-surfaces': {
                url: {
                    default: '/data/quantitative-analytics-curves-and-surfaces/v1',
                    doc: 'Base url path for the "curves-and-surfaces" content object',
                    format: String,
                },
                endpoints: {
                    'forward-curves': {
                        default: '/curves/forward-curves',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "forward-curves" data',
                        format: String,
                    },
                    surfaces: {
                        default: '/surfaces',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "surfaces" data',
                        format: String,
                    },
                    'zc-curves': {
                        default: '/curves/zc-curves',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "zc-curves" data',
                        format: String,
                    },
                    'zc-curve-definitions': {
                        default: '/curves/zc-curve-definitions',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "zc-curve-definitions" data',
                        format: String,
                    },
                },
            },
            'quantitative-analytics-dates-and-calendars': {
                url: {
                    default: '/data/quantitative-analytics-dates-and-calendars/v1',
                    doc: 'Base url path for the "dates-and-calendars" content object',
                    format: String,
                },
                endpoints: {
                    'add-periods': {
                        default: '/add-periods',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "add-periods" data',
                        format: String,
                    },
                    c3: {
                        default: '/c3',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "c3" data',
                        format: String,
                    },
                    'count-periods': {
                        default: '/count-periods',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "count-periods" data',
                        format: String,
                    },
                    'date-schedule': {
                        default: '/date-schedule',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "date-schedule" data',
                        format: String,
                    },
                    holidays: {
                        default: '/holidays',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "holidays" data',
                        format: String,
                    },
                    'is-working-day': {
                        default: '/is-working-day',
                        doc: 'Subpath for the Quantitative Analytics endpoint to retrieve the "is-working-day" data',
                        format: String,
                    },
                },
            },
            news: {
                url: {
                    default: '/data/news/v1',
                    doc: 'Base url path for the "news" content object',
                    format: String,
                },
                endpoints: {
                    headlines: {
                        default: '/headlines',
                        doc: 'Subpath for the News endpoint to retrieve the "headlines" data',
                        format: String,
                    },
                    stories: {
                        default: '/stories',
                        doc: 'Subpath for the News endpoint to retrieve the "stories" data',
                        format: String,
                    },
                },
            },
            'environmental-social-governance': {
                url: {
                    default: '/data/environmental-social-governance/v1',
                    doc: 'Base url path for the "environmental-social-governance" content object',
                    format: String,
                },
                endpoints: {
                    universe: {
                        default: '/universe',
                        doc: 'Subpath for the Environmental Social Governance endpoint to retrieve the "universe" data',
                        format: String,
                    },
                    basic: {
                        default: '/views/basic',
                        doc: 'Subpath for the Environmental Social Governance endpoint to retrieve the "basic" data',
                        format: String,
                    },
                    'measures-full': {
                        default: '/views/measures-full',
                        doc: 'Subpath for the Environmental Social Governance endpoint to retrieve the "measures-full" data',
                        format: String,
                    },
                    'measures-standard': {
                        default: '/views/measures-standard',
                        doc: 'Subpath for the Environmental Social Governance endpoint to retrieve the "measures-standard" data',
                        format: String,
                    },
                    'scores-full': {
                        default: '/views/scores-full',
                        doc: 'Subpath for the Environmental Social Governance endpoint to retrieve the "scores-full" data',
                        format: String,
                    },
                    'scores-standard': {
                        default: '/views/scores-standard',
                        doc: 'Subpath for the Environmental Social Governance endpoint to retrieve the "scores-standard" data',
                        format: String,
                    },
                },
            },
            datagrid: {
                url: {
                    default: '/api/udf',
                    doc: 'Base url path for the "datagrid" content object',
                    format: String,
                },
                endpoints: {
                    standard: {
                        default: '/',
                        doc: 'Subpath for the Datagrid endpoint to retrieve the "standard" data',
                        format: String,
                    },
                },
                'underlying-platform': {
                    default: types_1.UnderlyingPlatform.UDF,
                    doc: 'Platform type',
                    format: [types_1.UnderlyingPlatform.RDP, types_1.UnderlyingPlatform.UDF],
                },
                layout: {
                    [types_1.UnderlyingPlatform.RDP]: {
                        output: {
                            default: 'Col,T|Va,Row,In,date|',
                            doc: 'Response output formatting',
                            format: String,
                        },
                    },
                    [types_1.UnderlyingPlatform.UDF]: {
                        layout: {
                            columns: {
                                default: [{ item: 'dataitem' }],
                                doc: 'Udf layout columns',
                                format: Array,
                            },
                            rows: {
                                default: [{ item: 'instrument' }, { item: 'date' }],
                                doc: 'Udf layout rows',
                                format: Array,
                            },
                        },
                    },
                },
            },
        },
        discovery: {
            search: {
                url: {
                    default: '/discovery/search/v1',
                    doc: 'Base url path for the "search" content object',
                    format: String,
                },
                endpoints: {
                    search: {
                        default: '/',
                        doc: 'Subpath for the Search endpoint to retrieve all the "search" data',
                        format: String,
                    },
                    lookup: {
                        default: '/lookup',
                        doc: 'Subpath for the Search endpoint to retrieve the "lookup" data',
                        format: String,
                    },
                    metadata: {
                        default: '/metadata/views',
                        doc: 'Subpath for the Search endpoint to retrieve the "metadata" data',
                        format: String,
                    },
                },
            },
        },
        tsi: {
            metadata: {
                url: {
                    default: '/',
                    doc: 'Base url path for the historical pricing metadata content objects',
                    format: String,
                },
                endpoints: {
                    'global-metadata': {
                        default: '/global-metadata',
                        doc: 'Subpath for the global-metadata endpoint to retrieve the "global-metadata" data',
                        format: String,
                    },
                    'instrument-metadata': {
                        default: '/instrument-metadata',
                        doc: 'Subpath for the instrument-metadata endpoint to retrieve the "instrument-metadata" data',
                        format: String,
                    },
                    partialbar: {
                        default: '/partialbar',
                        doc: 'Subpath for the partialbar endpoint to retrieve the "partialbar" data',
                        format: String,
                    },
                    viewlist: {
                        default: '/viewlist',
                        doc: 'Subpath for the viewlist endpoint to retrieve the "viewlist" data',
                        format: String,
                    },
                },
            },
        },
        streaming: {
            pricing: {
                url: {
                    default: '/streaming/pricing/v1',
                    doc: 'Base url for the streaming pricing service discovery',
                    format: String,
                },
                endpoints: {
                    main: {
                        locations: {
                            default: [],
                            doc: 'Locations list allowed for this type of streaming connection',
                            format: Array,
                        },
                        path: {
                            default: '/',
                            doc: 'Service path',
                            format: String,
                        },
                        protocols: {
                            default: [types_1.StreamingType.OMM],
                            doc: 'Streaming protocol types',
                            format: Array,
                        },
                        'websocket-url': {
                            default: '',
                            doc: 'Allows to force the websocket URL. When this parameter is set, the library does not need to send a request to the discovery endpoint but should just use this url to open the websocket',
                            format: String,
                        },
                    },
                },
            },
            'quantitative-analytics': {
                url: {
                    default: '/streaming/quantitative-analytics/beta1',
                    doc: 'Base url for the QPS service discovery',
                    format: String,
                },
                endpoints: {
                    'financial-contracts': {
                        locations: {
                            default: [],
                            doc: 'Locations list allowed for this type of streaming connection',
                            format: Array,
                        },
                        path: {
                            default: '/financial-contracts',
                            doc: 'Service path',
                            format: String,
                        },
                        protocols: {
                            default: [types_1.StreamingType.RDP],
                            doc: 'Streaming protocol types',
                            format: Array,
                        },
                        'websocket-url': {
                            default: '',
                            doc: 'Allows to force the websocket URL. When this parameter is set, the library does not need to send a request to the discovery endpoint but should just use this url to open the websocket',
                            format: String,
                        },
                    },
                },
            },
            benchmark: {
                url: {
                    default: '/streaming/benchmark/v1',
                    doc: 'Base url for the Benchmark service discovery',
                    format: String,
                },
                endpoints: {
                    resource: {
                        locations: {
                            default: [],
                            doc: 'Locations list allowed for this type of streaming connection',
                            format: Array,
                        },
                        path: {
                            default: '/resource',
                            doc: 'Service path',
                            format: String,
                        },
                        protocols: {
                            default: [types_1.StreamingType.RDP],
                            doc: 'Streaming protocol types',
                            format: Array,
                        },
                        'websocket-url': {
                            default: '',
                            doc: 'Allows to force the websocket URL. When this parameter is set, the library does not need to send a request to the discovery endpoint but should just use this url to open the websocket',
                            format: String,
                        },
                    },
                },
            },
            'custom-instruments': {
                url: {
                    default: '/streaming/custom-instruments/v1',
                    doc: 'Base url for the streaming custom instruments service discovery',
                    format: String,
                },
                endpoints: {
                    resource: {
                        locations: {
                            default: [],
                            doc: 'Locations list allowed for this type of streaming connection',
                            format: Array,
                        },
                        path: {
                            default: '/resource',
                            doc: 'Service path',
                            format: String,
                        },
                        protocols: {
                            default: [types_1.StreamingType.OMM],
                            doc: 'Streaming protocol types',
                            format: Array,
                        },
                        'websocket-url': {
                            default: '',
                            doc: 'Allows to force the websocket URL. When this parameter is set, the library does not need to send a request to the discovery endpoint but should just use this url to open the websocket',
                            format: String,
                        },
                    },
                },
            },
        },
    },
};
//# sourceMappingURL=data:application/json;base64,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